import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Backend } from '@k2/common/backend/backend';
import { Exception } from '@k2/common/entities-state/types';
import { isNotNil, Subscriptions } from '@k2/common/helpers';
import { Paginator } from '@k2/common/pagination/paginator';
import { formSpecToFields } from '@k2/common/k2-forms/fields';
import { first, map } from 'rxjs/operators';
import { ClientAssignmentComponent } from '@k2/staff/content/clients/client/assignment/client-assignment.component';
import { FormDialogService } from '@k2/common/k2-forms/form-dialog/form-dialog.service';
import { ServiceIdentity } from '@k2/staff/content/clients/client/assignment/common/service-identity';
import { ExceptionsFieldsComponent } from '@k2/staff/content/clients/client/assignment/common/exceptions/exceptions-fields.component';
import { AppStore } from '@k2/common/state/services/app-store';
import { StaffAppState } from '@k2/staff/staff-app-state';
import { getAssignment } from '@k2/common/assignments-state/reducers';
import { select } from '@ngrx/store';
import { getAssignmentServiceInstanceIdFromServiceId } from '@k2/common/assignments-state/selectors';
import {BehaviorSubject, takeWhile} from 'rxjs';

@Component({
  selector: 'exceptions-section',
  templateUrl: 'exceptions-section.component.html',
  styleUrls: ['exceptions-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ExceptionsSectionComponent implements OnInit, OnDestroy {

  @Input() exceptions: Exception[];
  @Input() serviceIdentity: ServiceIdentity;
  @Input() serviceId: number;
  @Output() update = new EventEmitter();
  @Output() delete = new EventEmitter();

  underConfirmation = false;
  subscriptions = new Subscriptions();
  paginator = Paginator.custom({ pageSize: 5, keepOffsetAfterLengthChange: true });
  isAssignmentEncrypted = new BehaviorSubject(true)

  assignmentServiceInstanceId: number;

  constructor(private dialog: FormDialogService,
              private backend: Backend,
              private assignmentRef: ClientAssignmentComponent,
              private store: AppStore<StaffAppState>) {
    store.pipe(
      select(getAssignment(this.assignmentRef.identity.assignmentId)),
      first(isNotNil),
      map(res => res.is_assignment_encrypted)
    ).subscribe(data => this.isAssignmentEncrypted.next(data));
  }

  ngOnInit() {
    this.store.pipe(
      takeWhile(() => this.assignmentServiceInstanceId === undefined),
      select(
        getAssignmentServiceInstanceIdFromServiceId(this.assignmentRef.identity.assignmentId, this.serviceId)
      )
    ).subscribe(assignmentServiceInstanceId => {
      this.assignmentServiceInstanceId = assignmentServiceInstanceId;
    });
  }

  addException(serviceId: number) {
    const fields = this.backend.serviceException
      .getFormCreateServiceException(this.assignmentServiceInstanceId)
      .pipe(
        map(data => data.form),
        formSpecToFields
      );

    this.dialog
      .openForm({
        title: 'New exception',
        submitLabel: 'Add exception',
        fields,
        fieldsCmp: {
          component: ExceptionsFieldsComponent,
          inputs: { fields }
        },
        onSubmit: value => this.backend.serviceException
          .createServiceException(this.assignmentServiceInstanceId, value).toPromise()
      }).subscribe(response => this.update.emit(response));
  }

  deleteException(exceptionId) {
    this.delete.emit({ ...this.assignmentRef.identity, serviceId: this.serviceId, exceptionId });
  }

  updateExceptions(exceptionId) {
    const fields = this.backend.serviceException
      .getFormUpdateServiceException(exceptionId)
      .pipe(map(data => data.form), formSpecToFields);

    this.dialog
      .openForm({
          title: 'Edit exception',
          submitLabel: 'Save changes',
          fields,
          fieldsCmp: {
            component: ExceptionsFieldsComponent,
            inputs: { fields }
          },
          onSubmit: value => this.backend.serviceException
            .updateServiceException(exceptionId, value).toPromise()
        }
      ).subscribe(exceptions => this.update.emit(exceptions));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
